// src/utils/poll.js
//
// startPolling({ fn, intervalMs, onMaxFailures, maxFailures }) -> cancel()
//
// - setTimeout-recursive (not setInterval) so a slow response doesn't queue requests.
// - Pauses while document.hidden; resumes via the visibilitychange listener.
// - fn returns a Promise that resolves to { shouldStop?: boolean }; helper stops if shouldStop is true.
// - Tracks consecutive failures; calls onMaxFailures() once after `maxFailures` (default 3) in a row.

export default function startPolling({
  fn,
  intervalMs = 3000,
  maxFailures = 3,
  onMaxFailures = () => {},
}) {
  let stopped = false;
  let timer = null;
  let consecutiveFailures = 0;

  // Declare cancel first so tick (declared next) can reference it.
  function cancel() {
    stopped = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    document.removeEventListener('visibilitychange', onVisibility); // eslint-disable-line no-use-before-define
  }

  // Declare tick before schedule so schedule can reference it.
  async function tick() {
    timer = null;
    if (stopped) return;
    if (document.hidden) return;

    try {
      const result = await fn();
      if (stopped) return;
      consecutiveFailures = 0;
      if (result?.shouldStop) {
        cancel();
        return;
      }
    } catch (err) {
      if (stopped) return;
      consecutiveFailures += 1;
      if (consecutiveFailures >= maxFailures) {
        cancel();
        try { onMaxFailures(err); } catch (_unused) { /* intentional */ }
        return;
      }
    }

    schedule(intervalMs); // eslint-disable-line no-use-before-define
  }

  function schedule(delay) {
    if (stopped) return;
    if (document.hidden) {
      timer = null;
      return;
    }
    timer = setTimeout(tick, delay);
  }

  function onVisibility() {
    if (!stopped && !document.hidden && timer === null) {
      schedule(0);
    }
  }

  document.addEventListener('visibilitychange', onVisibility);

  schedule(intervalMs);

  return cancel;
}
